import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { propertySearchService } from '../../services/aiAssistantService';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';

/**
 * PropertySearchChat Component
 * Chat interface for property search (non-authenticated users and clients in search mode)
 */
const PropertySearchChat = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const {
    searchMessages,
    setSearchMessages,
    searchProperties,
    setSearchProperties,
    isLoading,
    setIsLoading,
    getCurrentSessionId,
    setCurrentSessionId,
    roleConfig,
    closeChatbot,
  } = useChatbot();

  const [localMessages, setLocalMessages] = useState([]);

  // Initialize with welcome message if no messages
  useEffect(() => {
    if (searchMessages.length === 0) {
      setSearchMessages([{
        role: 'assistant',
        content: roleConfig.welcomeMessage,
        timestamp: new Date(),
      }]);
    }
  }, []);

  // Sync local messages with context
  useEffect(() => {
    setLocalMessages(searchMessages);
  }, [searchMessages]);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [localMessages, searchProperties, scrollToBottom]);

  // Handle sending message
  const handleSendMessage = async (message) => {
    if (!message.trim() || isLoading) return;

    // Add user message
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    setSearchMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await propertySearchService.sendMessage(
        message,
        getCurrentSessionId()
      );

      setCurrentSessionId(response.session_id);
      setSearchProperties(response.properties || []);

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
      };

      setSearchMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Search chat error:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: 'Désolé, une erreur s\'est produite. Pouvez-vous reformuler votre recherche ?',
        timestamp: new Date(),
      };
      
      setSearchMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle property click
  const handlePropertyClick = (propertyId) => {
    closeChatbot();
    navigate(`/properties/${propertyId}`);
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      maximumFractionDigits: 0,
    }).format(price) + ' FCFA';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gradient-to-b from-gray-50 to-white min-h-0">
        {localMessages.map((msg, index) => (
          <ChatMessage
            key={index}
            message={msg}
            isUser={msg.role === 'user'}
            variant="search"
          />
        ))}

        {/* Property Results */}
        {searchProperties.length > 0 && (
          <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <p className="text-xs font-medium text-gray-500 px-1">
              Résultats ({searchProperties.length} bien{searchProperties.length > 1 ? 's' : ''})
            </p>
            {searchProperties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onClick={() => handlePropertyClick(property.id)}
                formatPrice={formatPrice}
              />
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && <ChatMessage isLoading />}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-gray-100 bg-white px-4 py-4">
        <ChatInput
          onSend={handleSendMessage}
          isLoading={isLoading}
          placeholder="Ex: Appartement 3 chambres Cocody"
          suggestions={localMessages.length <= 1 ? roleConfig.suggestions : []}
          variant="search"
        />
      </div>
    </div>
  );
};

/**
 * PropertyCard Component
 * Display a single property result
 */
const PropertyCard = ({ property, onClick, formatPrice }) => {
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-100 rounded-xl p-3 cursor-pointer hover:shadow-md hover:border-orange-200 transition-all duration-200 group"
    >
      {/* Property Image */}
      {property.images && property.images[0] && (
        <div className="relative mb-3 overflow-hidden rounded-lg">
          <img
            src={property.images[0]}
            alt={property.title}
            className="w-full h-28 object-cover transform group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 text-xs font-medium bg-white/90 backdrop-blur-sm rounded-full text-gray-700">
              {property.transaction_type === 'rent' ? 'Location' : 'Vente'}
            </span>
          </div>
        </div>
      )}

      {/* Property Info */}
      <h4 className="font-semibold text-sm text-gray-900 mb-1.5 line-clamp-1 group-hover:text-orange-600 transition-colors">
        {property.title}
      </h4>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center text-xs text-gray-500">
          <MapPin className="h-3 w-3 mr-1 text-gray-400" />
          <span>{property.city}</span>
        </div>
        <div className="text-sm font-bold text-orange-600">
          {formatPrice(property.price)}
          {property.transaction_type === 'rent' && (
            <span className="text-xs font-normal text-gray-400">/mois</span>
          )}
        </div>
      </div>

      {/* Property details */}
      {(property.rooms || property.surface_area) && (
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
          {property.rooms && <span>{property.rooms} pièces</span>}
          {property.bedrooms && <span>{property.bedrooms} ch.</span>}
          {property.surface_area && <span>{property.surface_area} m²</span>}
        </div>
      )}
    </div>
  );
};

export default PropertySearchChat;

